import { RefObject, type ComponentType, type ReactNode } from 'react';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  HeartIcon,
  UserIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import BirdSelector from './BirdSelector';
import { BirdCertificate } from '../../services/reports';
import { safeFormatDate } from '../../utils/dateUtils';
import {
  buildCertificateFilename,
  buildTransferPackage,
  downloadBlob,
} from '../../utils/birdCertificateShare';
import './birdCertificate.css';

interface BirdCertificateReportProps {
  selectedBirdId: string | null;
  onBirdSelect: (id: string | null) => void;
  data: BirdCertificate | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
  printRef: RefObject<HTMLDivElement | null>;
  shareUrl?: string;
}

function CertField({
  label,
  value,
  mono,
  emphasis,
  italic,
  className = '',
}: {
  label: string;
  value?: ReactNode;
  mono?: boolean;
  emphasis?: boolean;
  italic?: boolean;
  className?: string;
}) {
  const display = value ?? '—';
  return (
    <div className={className}>
      <p className="cert-field-label">{label}</p>
      <p
        className={[
          'cert-field-value',
          mono ? 'mono' : '',
          emphasis ? 'emphasis' : '',
          italic ? 'italic' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {display}
      </p>
    </div>
  );
}

function CertSection({
  title,
  icon: Icon,
  children,
  className = '',
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`cert-section ${className}`.trim()}>
      <div className="cert-section-head">
        <Icon className="cert-section-icon" aria-hidden />
        <h2 className="cert-section-title">{title}</h2>
      </div>
      <div className="cert-section-body">{children}</div>
    </section>
  );
}

function ComprehensiveBirdCertificate({ cert }: { cert: BirdCertificate }) {
  const bird = cert.bird;
  const identification = bird.identification as Record<string, string | undefined>;
  const biological = bird.biologicalInfo as unknown as Record<string, string | undefined>;
  const status = bird.status as unknown as Record<string, string | undefined>;
  const provenance = bird.provenance as Record<string, unknown>;
  const lineage = bird.lineage as Record<string, unknown>;
  const facility = (cert as { facility?: Record<string, string> }).facility;
  const valuation = (cert as { valuation?: { estimatedValue?: number; factors?: string[] } }).valuation;
  const photos = (bird as { photos?: Array<{ id?: string; url?: string; caption?: string; dateAdded?: string }> }).photos;
  const healthRecords = (bird as { healthRecords?: Record<string, unknown> }).healthRecords;

  const formatMoney = (amount?: number, currency = 'ZAR') =>
    amount != null ? `${currency} ${amount.toLocaleString()}` : '—';

  return (
    <div className="bird-certificate-document px-6 py-8 sm:px-8 print:px-0 print:py-0">
      <header className="cert-header">
        <div className="cert-header-badge" aria-hidden>
          <ShieldCheckIcon className="h-7 w-7" />
        </div>
        <h1 className="cert-title">Official Bird Certificate</h1>
        <p className="cert-subtitle">Breeding &amp; Registration Document</p>
      </header>

      <div className="cert-meta-grid">
        <div className="cert-meta-card">
          <p className="cert-meta-label">Certificate No.</p>
          <p className="cert-meta-value mono">{cert.certificateNumber}</p>
        </div>
        <div className="cert-meta-card">
          <p className="cert-meta-label">Issue Date</p>
          <p className="cert-meta-value">{safeFormatDate(cert.issueDate)}</p>
        </div>
        <div className="cert-meta-card">
          <p className="cert-meta-label">Valid Until</p>
          <p className="cert-meta-value">{safeFormatDate(cert.validUntil)}</p>
        </div>
      </div>

      <div className="cert-two-col mb-5">
        <CertSection title="Bird Identification" icon={ClipboardDocumentCheckIcon}>
          <div className="cert-field-grid">
            <CertField
              label="Band Number"
              value={bird.bandNumber || identification?.bandId}
              mono
              emphasis
            />
            <CertField label="Microchip" value={identification?.microchipId || 'No microchip recorded'} />
            <CertField label="DNA Profile" value={identification?.dnaProfile || 'Not recorded'} className="span-full" />
            <CertField
              label="Distinguishing Marks"
              value={identification?.distinguishingMarks || 'None noted'}
              className="span-full"
            />
          </div>
        </CertSection>

        <CertSection title="Species Information" icon={DocumentTextIcon}>
          <div className="cert-field-grid">
            <CertField label="Common Name" value={bird.species?.commonName || 'Unknown species'} emphasis className="span-full" />
            <CertField label="Scientific Name" value={bird.species?.scientificName || 'Not available'} italic className="span-full" />
            <CertField label="Family" value={bird.species?.family || 'Not specified'} />
            <CertField label="Order" value={bird.species?.order || 'Not specified'} />
            <CertField label="IUCN Status" value={bird.species?.conservationStatus || 'Not specified'} />
            <CertField label="CITES" value={bird.species?.citesStatus || 'Not specified'} />
            <CertField label="Native Habitat" value={bird.species?.nativeHabitat || 'Not specified'} />
            <CertField label="Average Lifespan" value={bird.species?.averageLifespan || 'Not specified'} />
          </div>
        </CertSection>
      </div>

      <CertSection title="Biological Information" icon={HeartIcon}>
        <div className="cert-field-grid cols-3">
          <CertField label="Sex" value={biological?.sex} emphasis />
          <CertField label="Current Age" value={biological?.age} emphasis />
          <CertField label="Current Status" value={status?.current} emphasis />
          <CertField label="Sex Determination" value={biological?.sexDeterminationMethod} />
          <CertField label="Color Mutation" value={biological?.colorMutation} />
          <CertField label="Location" value={status?.location} />
          <CertField label="Hatch Date" value={safeFormatDate(biological?.hatchDate)} />
          <CertField label="Phenotype" value={biological?.phenotype} />
          <CertField label="Health Status" value={status?.health} />
        </div>
      </CertSection>

      <CertSection title="Provenance & Acquisition" icon={ShieldCheckIcon}>
        <div className="cert-field-grid cols-3">
          <CertField label="Origin" value={provenance?.origin as string} />
          <CertField
            label="Acquisition Date"
            value={
              provenance?.dateAcquired
                ? safeFormatDate(String(provenance.dateAcquired), 'PPP')
                : 'Not specified'
            }
          />
          <CertField label="Type" value={provenance?.type as string} />
          {provenance?.sellerName ? (
            <div className="span-full cert-subsection">
              <p className="cert-subsection-title">Previous Owner / Seller</p>
              <p className="cert-field-value emphasis">{String(provenance.sellerName)}</p>
              {provenance.sellerContact ? (
                <p className="cert-field-value text-sm">{String(provenance.sellerContact)}</p>
              ) : null}
              {provenance.sellerAddress ? (
                <p className="cert-field-value text-sm">{String(provenance.sellerAddress)}</p>
              ) : null}
            </div>
          ) : null}
          {provenance?.purchasePrice != null ? (
            <CertField
              label="Purchase Price"
              value={formatMoney(Number(provenance.purchasePrice), String(provenance.currency || 'ZAR'))}
            />
          ) : null}
          {provenance?.invoiceNumber ? (
            <CertField label="Invoice Number" value={String(provenance.invoiceNumber)} />
          ) : null}
          {provenance?.purchaseOrderNumber ? (
            <CertField label="Purchase Order" value={String(provenance.purchaseOrderNumber)} />
          ) : null}
          {provenance?.paymentMethod ? (
            <CertField label="Payment Method" value={String(provenance.paymentMethod)} />
          ) : null}
          {provenance?.transactionId ? (
            <CertField label="Transaction ID" value={String(provenance.transactionId)} mono />
          ) : null}
          {provenance?.batchNumber ? (
            <CertField label="Batch Number" value={String(provenance.batchNumber)} />
          ) : null}
          <div className="span-full cert-subsection">
            <p className="cert-subsection-title">Quarantine Status</p>
            <p className="cert-field-value emphasis">{String(provenance?.quarantineStatus || 'Not required')}</p>
            {provenance?.quarantineStartDate ? (
              <p className="cert-field-value text-sm">
                Start: {safeFormatDate(String(provenance.quarantineStartDate), 'PPP')}
              </p>
            ) : null}
            {provenance?.quarantineEndDate ? (
              <p className="cert-field-value text-sm">
                End: {safeFormatDate(String(provenance.quarantineEndDate), 'PPP')}
              </p>
            ) : null}
            {provenance?.quarantinePeriod ? (
              <p className="cert-field-value text-sm">Period: {String(provenance.quarantinePeriod)} days</p>
            ) : null}
          </div>
          {provenance?.importPermitNumber ? (
            <CertField label="Import Permit" value={String(provenance.importPermitNumber)} />
          ) : null}
          {provenance?.healthCertificateNumber ? (
            <CertField label="Health Certificate" value={String(provenance.healthCertificateNumber)} />
          ) : null}
          {provenance?.microchipId ? (
            <CertField label="Microchip ID" value={String(provenance.microchipId)} mono />
          ) : null}
          {provenance?.warrantyInfo ? (
            <CertField label="Warranty" value={String(provenance.warrantyInfo)} className="span-full" />
          ) : null}
          {provenance?.notes ? (
            <CertField label="Additional Notes" value={String(provenance.notes)} className="span-full" />
          ) : null}
        </div>
      </CertSection>

      <CertSection title="Lineage & Family Tree" icon={UserIcon}>
        <div className="cert-lineage-grid mb-4">
          <div>
            <p className="cert-subsection-title">Parents</p>
            <div className="cert-card-list">
              {(lineage?.father as Record<string, unknown>) ? (
                <div className="cert-subsection">
                  <p className="cert-field-label">Father</p>
                  <p className="cert-field-value emphasis mono">
                    {String((lineage.father as Record<string, unknown>).bandNumber)}
                  </p>
                  <p className="cert-field-value text-sm">
                    {String(
                      ((lineage.father as Record<string, { commonName?: string; scientificName?: string }>).species
                        ?.commonName ||
                        (lineage.father as Record<string, { scientificName?: string }>).species?.scientificName) ??
                        'Unknown species',
                    )}
                  </p>
                </div>
              ) : (
                <div className="cert-subsection">
                  <p className="cert-field-value text-sm">Father information not recorded</p>
                </div>
              )}
              {(lineage?.mother as Record<string, unknown>) ? (
                <div className="cert-subsection">
                  <p className="cert-field-label">Mother</p>
                  <p className="cert-field-value emphasis mono">
                    {String((lineage.mother as Record<string, unknown>).bandNumber)}
                  </p>
                  <p className="cert-field-value text-sm">
                    {String(
                      ((lineage.mother as Record<string, { commonName?: string; scientificName?: string }>).species
                        ?.commonName ||
                        (lineage.mother as Record<string, { scientificName?: string }>).species?.scientificName) ??
                        'Unknown species',
                    )}
                  </p>
                </div>
              ) : (
                <div className="cert-subsection">
                  <p className="cert-field-value text-sm">Mother information not recorded</p>
                </div>
              )}
            </div>
          </div>
          <div>
            <p className="cert-subsection-title">
              Siblings ({Array.isArray(lineage?.siblings) ? lineage.siblings.length : 0})
            </p>
            <div className="cert-card-list scroll-screen">
              {Array.isArray(lineage?.siblings) && lineage.siblings.length > 0 ? (
                (lineage.siblings as Array<Record<string, unknown>>).map((sibling, index) => (
                  <div key={String(sibling.id || index)} className="cert-subsection">
                    <p className="cert-field-value emphasis">
                      {String(sibling.bandNumber)} ({String(sibling.sex)})
                    </p>
                    <p className="cert-field-value text-sm">
                      {String(
                        (sibling.species as { commonName?: string; scientificName?: string })?.commonName ||
                          (sibling.species as { scientificName?: string })?.scientificName ||
                          'Unknown species',
                      )}
                    </p>
                  </div>
                ))
              ) : (
                <div className="cert-subsection">
                  <p className="cert-field-value text-sm">No sibling information available</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {Array.isArray(lineage?.offspring) && lineage.offspring.length > 0 ? (
          <div>
            <p className="cert-subsection-title mb-2">Offspring ({lineage.offspring.length})</p>
            <div className="cert-field-grid cols-3">
              {(lineage.offspring as Array<Record<string, unknown>>).map((offspring, index) => (
                <div key={String(offspring.id || index)} className="cert-subsection">
                  <p className="cert-field-value emphasis">
                    {String(offspring.bandNumber)} ({String(offspring.sex)})
                  </p>
                  <p className="cert-field-value text-sm">
                    {String(
                      (offspring.species as { commonName?: string; scientificName?: string })?.commonName ||
                        (offspring.species as { scientificName?: string })?.scientificName ||
                        'Unknown species',
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CertSection>

      {healthRecords ? (
        <CertSection title="Health Records" icon={HeartIcon}>
          <div className="cert-field-grid">
            <div>
              <p className="cert-subsection-title">Latest Health Status</p>
              {(healthRecords.lastExamination as Record<string, string>) ? (
                <div className="cert-subsection space-y-2">
                  <CertField
                    label="Last Examination"
                    value={safeFormatDate((healthRecords.lastExamination as Record<string, string>).date)}
                  />
                  <CertField
                    label="Test Type"
                    value={(healthRecords.lastExamination as Record<string, string>).testType}
                  />
                  <CertField
                    label="Result"
                    value={(healthRecords.lastExamination as Record<string, string>).result}
                  />
                  <CertField
                    label="Veterinarian"
                    value={(healthRecords.lastExamination as Record<string, string>).veterinarian}
                  />
                </div>
              ) : (
                <div className="cert-subsection">
                  <p className="cert-field-value text-sm">No recent health examination records</p>
                </div>
              )}
              <div className="mt-3">
                <CertField label="Vaccination Status" value={String(healthRecords.vaccinationStatus || '—')} />
              </div>
            </div>
            <div>
              <p className="cert-subsection-title">Health Concerns</p>
              {Array.isArray(healthRecords.healthConcerns) && healthRecords.healthConcerns.length > 0 ? (
                <div className="cert-card-list">
                  {(healthRecords.healthConcerns as string[]).map((concern, index) => (
                    <div key={index} className="cert-subsection">
                      <p className="cert-field-value text-sm">{concern}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="cert-subsection">
                  <p className="cert-field-value text-sm">No health concerns noted</p>
                </div>
              )}
            </div>
          </div>
        </CertSection>
      ) : null}

      <CertSection title="Breeder & Facility Information" icon={BuildingOfficeIcon}>
        <div className="cert-field-grid">
          <CertField label="Facility Name" value={facility?.name || 'Not specified'} emphasis />
          <CertField label="License Number" value={facility?.license || 'Not specified'} mono />
          <CertField label="Contact Information" value={facility?.contact || 'Not specified'} />
          <CertField label="Address" value={facility?.address || 'Not specified'} />
        </div>
      </CertSection>

      {valuation ? (
        <CertSection title="Valuation Information" icon={CurrencyDollarIcon}>
          <div className="cert-field-grid">
            <div className="cert-highlight-box">
              <p className="cert-field-label">Estimated Value</p>
              <p className="cert-value-highlight">R {valuation.estimatedValue?.toLocaleString() ?? '—'}</p>
            </div>
            <div>
              <p className="cert-subsection-title">Valuation Factors</p>
              <div className="cert-card-list">
                {(valuation.factors || []).length > 0 ? (
                  (valuation.factors || []).map((factor, index) => (
                    <div key={index} className="cert-subsection flex items-start gap-2">
                      <ShieldCheckIcon className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <p className="cert-field-value text-sm">{factor}</p>
                    </div>
                  ))
                ) : (
                  <div className="cert-subsection">
                    <p className="cert-field-value text-sm">No valuation factors recorded</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CertSection>
      ) : null}

      {photos && photos.length > 0 ? (
        <CertSection title={`Photos (${photos.length})`} icon={PhotoIcon}>
          <div className="cert-photo-grid">
            {photos.map((photo, index) => (
              <div key={photo.id || index} className="cert-photo-card cert-subsection">
                {photo.url ? (
                  <img
                    src={photo.url}
                    alt={photo.caption || `Photo ${index + 1}`}
                    loading="lazy"
                    onClick={() => window.open(photo.url, '_blank')}
                  />
                ) : (
                  <div className="flex items-center justify-center bg-slate-100 aspect-square">
                    <PhotoIcon className="h-10 w-10 text-slate-400" />
                  </div>
                )}
                {(photo.caption || photo.dateAdded) && (
                  <div className="cert-photo-caption">
                    {photo.caption ? <p className="truncate">{photo.caption}</p> : null}
                    {photo.dateAdded ? (
                      <p>{safeFormatDate(photo.dateAdded, 'MMM dd, yyyy')}</p>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CertSection>
      ) : null}

      <footer className="cert-footer">
        <div className="cert-signature-grid">
          <div>
            <div className="cert-signature-line" />
            <p className="cert-field-label">Authorized Representative</p>
            <p className="cert-field-value text-sm">{facility?.name || 'Not specified'}</p>
          </div>
          <div>
            <div className="cert-signature-line" />
            <p className="cert-field-label">Date</p>
          </div>
        </div>
        <div className="cert-disclaimer">
          <p>This certificate is valid for one year from the issue date and certifies the information above to be true and accurate.</p>
          <p className="mt-1">Certificate Number: {cert.certificateNumber}</p>
        </div>
      </footer>
    </div>
  );
}

export default function BirdCertificateReport({
  selectedBirdId,
  onBirdSelect,
  data,
  loading,
  error,
  onReload,
  printRef,
  shareUrl,
}: BirdCertificateReportProps) {
  const handleExportTransferPackage = () => {
    if (!data) return;
    const pkg = buildTransferPackage(data, {
      sourceInstance: typeof window !== 'undefined' ? window.location.host : undefined,
      sourceUrl: shareUrl,
    });
    const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
    downloadBlob(blob, `${buildCertificateFilename(data)}.birdman.json`);
    toast.success('Transfer file downloaded — buyer can Import on Birds');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div>
              <h3 className="text-lg leading-6 font-medium text-slate-900">Bird Selection</h3>
              <p className="mt-1 text-sm text-slate-500">
                Share uses a deep link to this bird. Recipients need access to this BirdMan instance
                (login / ACL). For buyers on another instance, export the transfer file.
              </p>
            </div>
            {data && (
              <button
                type="button"
                onClick={handleExportTransferPackage}
                className="btn btn-secondary text-sm inline-flex items-center print:hidden"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Export transfer file
              </button>
            )}
          </div>
          <BirdSelector
            selectedBirdId={selectedBirdId}
            onBirdSelect={onBirdSelect}
            onClear={() => onBirdSelect(null)}
          />
        </div>
      </div>

      {!selectedBirdId ? (
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No bird selected</h3>
              <p className="mt-1 text-sm text-slate-500">Select a bird above to generate a comprehensive certificate.</p>
            </div>
          </div>
        </div>
      ) : loading ? (
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
              <p className="mt-2 text-sm text-slate-600">Generating certificate...</p>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">Error generating certificate</h3>
              <p className="mt-1 text-sm text-red-600">{error}</p>
              <button
                onClick={onReload}
                className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-cyan-700 bg-cyan-100 hover:bg-indigo-200"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      ) : data ? (
        <div ref={printRef} className="bg-white overflow-hidden shadow rounded-lg print:shadow-none print:border-none print:overflow-visible">
          <ComprehensiveBirdCertificate cert={data} />
        </div>
      ) : null}
    </div>
  );
}
